import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { authenticatedSessionEstablished, clearAuthenticatedSession, sessionExpired } from "@/features/auth/authSessionSlice";

import type { RootState } from "@/app/store";

interface WorkspaceStoreSeedSelection {
  organizationId: string;
  storeBindingId: string;
}

export interface ActiveStoreState {
  selectedByOrganization: Record<string, string>;
  userId: string;
}

const initialState: ActiveStoreState = {
  selectedByOrganization: {},
  userId: "",
};

const activeStoreSlice = createSlice({
  initialState,
  name: "activeStore",
  extraReducers: builder => {
    builder.addCase(authenticatedSessionEstablished, (state, action) => {
      if (state.userId !== action.payload.actor.userId) state.selectedByOrganization = {};
      state.userId = action.payload.actor.userId;
    });
    builder.addCase(clearAuthenticatedSession, () => initialState);
    builder.addCase(sessionExpired, () => initialState);
  },
  reducers: {
    activeStoreCleared(state, action: PayloadAction<{ organizationId: string }>) {
      delete state.selectedByOrganization[action.payload.organizationId];
    },
    activeStoreSelected(
      state,
      action: PayloadAction<WorkspaceStoreSeedSelection>,
    ) {
      const organizationId = action.payload.organizationId.trim();
      const storeBindingId = action.payload.storeBindingId.trim();
      if (!organizationId || !storeBindingId) return;
      state.selectedByOrganization[organizationId] = storeBindingId;
    },
  },
});

export const { activeStoreSelected, activeStoreCleared } = activeStoreSlice.actions;
export const workspaceStoreSeedSelected = activeStoreSelected;

export const selectWorkspaceStoreSeedBindingId = (
  state: RootState,
  organizationId: string,
) => state.activeStore.selectedByOrganization[organizationId] ?? "";

export const selectActiveStoreBindingId = selectWorkspaceStoreSeedBindingId;

export default activeStoreSlice.reducer;
